/*
 * Publishes `--bh-dock-height` on <html>: how many pixels of bottom-docked UI are currently
 * covering the viewport bottom. The floating buttons (chat bubble, back-to-top) add it to
 * their own `bottom` so they sit ABOVE a sticky bar instead of on top of it.
 *
 * The bars marked data-bh-dock behave differently from each other AND from themselves at
 * different widths, so a hardcoded height per page would be wrong the first time any changed:
 *   - .bh-actions      PDP add-to-cart: `fixed` below 1068px, `sticky` above it
 *   - .bh-cart-sticky  cart checkout bar: slides in and out, display:none from 750px up
 *   - (anything future marked data-bh-dock)
 *
 * Measured as "how much of the element is on screen" rather than "is it visible":
 * `innerHeight - rect.top` is 0 for a bar translated off the bottom edge, which means the
 * animated cart bar needs no class-watching and reads correctly MID-transition too. One
 * mechanism covers both the static bar and the animated one.
 */
(() => {
  const root = document.documentElement;
  let queued = false;

  function measure() {
    queued = false;
    let covered = 0;
    for (const el of document.querySelectorAll('[data-bh-dock]')) {
      // Only `fixed` counts as docked. .bh-actions is `position: sticky; bottom: 0` on
      // desktop and only becomes `fixed` below 1068px — a sticky element is still in flow,
      // the page already reserves its space, and nothing needs to move out of its way. Without
      // this check it was reported as a dock the moment it rode the bottom of its column,
      // which is why both buttons drifted upward part-way down a desktop PDP.
      if (getComputedStyle(el).position !== 'fixed') continue;
      const r = el.getBoundingClientRect();
      if (r.height === 0) continue;
      covered = Math.max(covered, Math.min(window.innerHeight - r.top, r.height));
    }
    root.style.setProperty('--bh-dock-height', Math.max(0, Math.round(covered)) + 'px');
  }

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  };

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });
  // The cart bar's own slide finishes after the last scroll event, so scrolling alone would
  // leave the offset one frame stale at rest.
  addEventListener('transitionend', schedule, true);

  // Fires once on observe, which is also the initial measurement, and again whenever a bar's
  // content changes its height (the cart total wrapping to two lines, for one).
  const ro = new ResizeObserver(schedule);
  for (const el of document.querySelectorAll('[data-bh-dock]')) ro.observe(el);
})();
